import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { resourceTypeLabel } from '../../../../core/utils/resource-type-label.util';
import {
  PoDialogService,
  PoModalAction,
  PoModalComponent,
  PoSelectOption,
  PoTableColumn
} from '@po-ui/ng-components';

import { ReviewDetailComponent } from '../../../../review/components/review-detail/review-detail.component';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import {
  categoriesByKind,
  categoryHint,
  leverDescription,
  leverLabel,
  LEVER_BY_CATEGORY
} from '../../curation-constants';
import {
  CurateRequest,
  CurationCategory,
  CurationCuratedEvent,
  CurationFeedback,
  CurationQueueItem,
  ItemIdentity
} from '../../interfaces/curation.interface';
import { CurationService } from '../../curation.service';
import { formatCurationReviewLabel, formatCurationDateTime, getCurationFeedbacks } from '../../utils/curation-format.util';
import { buildCurationSnapshotView, CurationSnapshotView } from '../../utils/curation-snapshot.util';
import {
  buildSeverityTag,
  DUE_SEVERITY_OPTIONS,
  DueSeverity,
  emittedSeverity,
  readDueSeverity,
  stripSeverityTag,
  withSeverityTag
} from '../../utils/curation-severity-tag.util';
import { getApiErrorMessage } from 'src/app/menu/smart-review/core/utils/http-error-message.util';
import { formatSeverityLabel, severityColor, SeverityColor } from 'src/app/menu/smart-review/review/utils/severity.util';
import {
  FINDING_RESOLUTIONS,
  OVERALL_RESOLUTIONS,
  ResolutionOption
} from 'src/app/menu/smart-review/review/review-feedback.constants';

/** Modal de classificação (curadoria) de um item da fila. */
@Component({
  selector: 'app-curation-modal',
  templateUrl: './curation-modal.component.html',
  styleUrls: ['./curation-modal.component.css']
})
export class CurationModalComponent {
  @ViewChild('modal', { static: true }) modal!: PoModalComponent;
  @ViewChild('reviewModal', { static: true }) reviewModal!: PoModalComponent;
  @ViewChild(ReviewDetailComponent, { static: true }) reviewDetail!: ReviewDetailComponent;

  /** Emitido após salvar com sucesso — a fila atualiza o badge e remove o item. */
  @Output() curated = new EventEmitter<CurationCuratedEvent>();

  /** Emitido após descartar com sucesso — a fila remove o item e ajusta contadores. */
  @Output() discarded = new EventEmitter<CurationQueueItem>();

  item: CurationQueueItem | null = null;
  categoryOptions: PoSelectOption[] = [];
  category: CurationCategory | '' = '';
  comment = '';
  /** Severidade em que o achado deveria ter saído — vira a etiqueta `[SEVERIDADE: …]`. */
  dueSeverity: DueSeverity | '' = '';
  lever = '';
  saving = false;
  discarding = false;
  snapshotView: CurationSnapshotView = buildCurationSnapshotView(null);
  // Avaliações dos devs já formatadas — montadas uma vez em open() (não em getter,
  // que rebuildaria o array e reformataria as datas a cada ciclo de detecção).
  feedbacks: CurationFeedback[] = [];

  readonly feedbackColumns: PoTableColumn[] = [
    { property: 'user', label: 'Avaliador', width: '220px' },
    { property: 'createdAtLabel', label: 'Avaliado em', width: '180px' },
    { property: 'resolution', label: 'Resolução', type: 'cellTemplate', width: '170px' },
    { property: 'comment', label: 'Comentário' }
  ];

  readonly reviewModalCloseAction: PoModalAction = {
    label: 'Fechar',
    action: () => this.reviewModal.close()
  };

  get primaryAction(): PoModalAction {
    return {
      label: 'Salvar',
      action: () => this.save(),
      loading: this.saving,
      disabled: this.saving || this.discarding || !this.category || this.mode === 'view'
    };
  }

  get secondaryAction(): PoModalAction {
    return {
      label: 'Descartar',
      danger: true,
      action: () => this.confirmDiscard(),
      loading: this.discarding,
      disabled: this.saving || this.discarding || this.isDiscarded || this.mode === 'view'
    };
  }

  constructor(
    private curationService: CurationService,
    private notification: NotificationServiceHM,
    private dialog: PoDialogService
  ) {}

  /** Modo de abertura: 'curate' mostra classificação editável; 'view' é read-only. */
  mode: 'curate' | 'view' = 'curate';

  /** Abre o modal carregando o item; pré-preenche ao reclassificar item já curado. */
  open(item: CurationQueueItem, mode: 'curate' | 'view' = 'curate'): void {
    this.item = item;
    this.mode = mode;
    this.snapshotView = buildCurationSnapshotView(item);
    this.categoryOptions = categoriesByKind(item.kind);
    this.category = (item.category as CurationCategory) || '';
    // A etiqueta de severidade sai do texto editável e volta pelo campo próprio: editar a
    // etiqueta à mão é o caminho para gravá-la num formato que o relatório não lê.
    this.comment = stripSeverityTag(item.curatorComment);
    this.dueSeverity = readDueSeverity(item.curatorComment, emittedSeverity(this.snapshotView.severity)) || '';
    this.lever = this.category ? (LEVER_BY_CATEGORY[this.category] || '—') : '';
    this.feedbacks = getCurationFeedbacks(item);
    this.saving = false;
    this.discarding = false;
    this.modal.open();
  }

  close(): void {
    this.modal.close();
  }

  get resourceLabel(): string {
    return this.item ? formatCurationReviewLabel(this.item) : '';
  }

  get isFinding(): boolean {
    return this.item?.kind === 'finding';
  }

  /** Severidade com que o achado saiu, normalizada para ALTA/MEDIA/BAIXA. */
  get emittedSeverity(): string {
    return emittedSeverity(this.snapshotView.severity);
  }

  /**
   * Ação obrigatória curada como correta ou com severidade errada leva a etiqueta de
   * severidade — a mesma regra do `sr_curate.py` da skill de curadoria.
   */
  get needsSeverityTag(): boolean {
    return this.isFinding && this.item?.findingKind === 'required_action'
      && (this.category === 'correct' || this.category === 'wrong_severity');
  }

  /** Só "severidade errada" pergunta a devida; "correto" já afirma que a emitida estava certa. */
  get asksDueSeverity(): boolean {
    return this.needsSeverityTag && this.category === 'wrong_severity';
  }

  /** As opções de severidade devida, sem a emitida — errada não pode ser ela mesma. */
  get dueSeverityOptions(): PoSelectOption[] {
    return DUE_SEVERITY_OPTIONS.filter(o => o.value !== this.emittedSeverity);
  }

  /** "Quando usar" da categoria selecionada — ajuda o curador a classificar. */
  get categoryHint(): string {
    return categoryHint(this.category);
  }

  /** Rótulo amigável da alavanca de melhoria derivada. */
  get leverLabel(): string {
    return leverLabel(this.lever) || this.lever;
  }

  /** O que o lever significa e onde acioná-lo. */
  get leverHint(): string {
    return leverDescription(this.lever);
  }

  get snapshotLine(): string {
    return this.snapshotView.line || 'n/d';
  }

  get snapshotSeverity(): string {
    return formatSeverityLabel(this.snapshotView.severity) || 'n/d';
  }

  get itemLanguage(): string {
    return this.item?.language?.trim() || '';
  }

  get fileName(): string {
    return this.item?.fileName?.trim() || '';
  }

  get filePath(): string {
    return this.item?.filePath?.trim() || '';
  }

  get curatedAt(): string {
    return formatCurationDateTime(this.item?.curatedAt);
  }

  get isCurated(): boolean {
    return this.item?.status === 'curated' || this.item?.status === 'discarded';
  }

  get isDiscarded(): boolean {
    return this.item?.status === 'discarded';
  }

  get showClassification(): boolean {
    return this.mode === 'curate';
  }

  /** Exibe aviso de reclassificação apenas quando abrindo um item descartado para curar. */
  get showReclassifyBanner(): boolean {
    return this.mode === 'curate' && this.isDiscarded;
  }

  get hasFeedbacks(): boolean {
    return this.feedbacks.length > 0;
  }

  get feedbackCountLabel(): string {
    const total = this.feedbacks.length;
    return total === 1 ? '1 avaliação dos devs' : `${total} avaliações dos devs`;
  }

  get snapshotDescription(): string {
    return this.snapshotView.description || 'n/d';
  }

  get snapshotAction(): string {
    return this.snapshotView.action;
  }

  get snapshotCode(): string {
    return this.snapshotView.code;
  }

  openOriginalReview(): void {
    if (!this.item) { return; }
    // Abre o detalhe do review num modal embutido (modalMode), sem sair da
    // curadoria nem trocar de aba (que perderia a sessão do sessionStorage).
    const findingId = this.item.kind !== 'review' ? (this.item.findingId || '') : '';
    this.reviewModal.open();
    this.reviewDetail.loadForModal(this.item.resourceType, this.item.resourceId, findingId);
  }

  getResourceTypeLabel(resourceType: string): string {
    return resourceTypeLabel(resourceType) || 'n/d';
  }

  getFindingKindLabel(findingKind: string): string {
    const labels: Record<string, string> = {
      required_action: 'Ação',
      suggestion: 'Sugestão',
      overall: 'Geral'
    };
    return labels[(findingKind || '').toLowerCase()] || findingKind || 'n/d';
  }

  // Mesma fonte do card de feedback do review (review-feedback.constants.ts):
  // achado usa FINDING_RESOLUTIONS; overall usa OVERALL_RESOLUTIONS.
  private resolutionOption(resolution: CurationFeedback['resolution']): ResolutionOption | undefined {
    const source = this.isFinding ? FINDING_RESOLUTIONS : OVERALL_RESOLUTIONS;
    return source.find(o => o.value === resolution);
  }

  getResolutionLabel(resolution: CurationFeedback['resolution']): string {
    return this.resolutionOption(resolution)?.label || resolution;
  }

  // p-color (hex) em vez de p-type: com p-type o po-tag trata p-icon como
  // booleano e só exibe o ícone padrão do tipo, ignorando o ícone do card.
  getResolutionColor(resolution: CurationFeedback['resolution']): string {
    return this.resolutionOption(resolution)?.color || '#9aa3a8';
  }

  getResolutionIcon(resolution: CurationFeedback['resolution']): string {
    return this.resolutionOption(resolution)?.icon || '';
  }

  // Reaproveita o mapa de cores de severidade do review (ALTA\u2192danger, MEDIA\u2192warning,
  // BAIXA/demais\u2192info), mantendo "Baixa" visualmente distinta de "M\u00e9dia".
  getSeverityType(severity: string): SeverityColor {
    return severityColor(severity);
  }

  /** Atualiza o lever derivado quando a categoria muda. */
  onCategoryChange(value: CurationCategory): void {
    this.category = value;
    this.lever = value ? LEVER_BY_CATEGORY[value] || '—' : '';
  }

  /** Pede confirmação e descarta o item. */  confirmDiscard(): void {
    if (!this.item) { return; }
    const resource = formatCurationReviewLabel(this.item);
    this.dialog.confirm({
      title: 'Descartar feedback',
      message: `Descartar o feedback de <strong>${resource}</strong>?`,
      confirm: () => this.discard(),
      cancel: () => {}
    });
  }

  private discard(): void {
    if (!this.item || this.discarding) { return; }
    const discardedItem = this.item;
    const body: ItemIdentity = {
      resourceType: discardedItem.resourceType,
      resourceId: discardedItem.resourceId,
      findingKind: discardedItem.findingKind,
      findingId: discardedItem.findingId
    };
    this.discarding = true;
    this.curationService.discardItem(body).subscribe({
      next: () => {
        this.discarding = false;
        this.notification.showToast('Feedback descartado.', 'success');
        this.modal.close();
        this.discarded.emit(discardedItem);
      },
      error: error => {
        this.discarding = false;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao descartar o feedback.'), 'error');
      }
    });
  }

  /**
   * O comentário com a etiqueta de severidade quando ela se aplica: `ok` em correto, a devida
   * em severidade errada. Nas demais categorias a etiqueta lida do comentário anterior é
   * mantida, para reclassificar não apagar o julgamento de severidade já feito.
   */
  private commentToSave(): string {
    if (this.category === 'correct' && this.needsSeverityTag) {
      return withSeverityTag(this.comment, '[SEVERIDADE: ok]');
    }
    if (this.dueSeverity && this.dueSeverity !== this.emittedSeverity && this.emittedSeverity) {
      return withSeverityTag(this.comment, buildSeverityTag(this.emittedSeverity, this.dueSeverity));
    }
    return this.comment;
  }

  save(): void {
    if (!this.item || !this.category) {
      this.notification.showToast('Selecione a categoria para salvar a curadoria.', 'warning');
      return;
    }
    if (this.saving) { return; }
    // Sem a severidade emitida no snapshot não há como montar `<emitida>→<devida>`: gravar
    // "severidade errada" sem etiqueta tiraria o item da matriz sem ninguém perceber.
    if (this.asksDueSeverity && !this.emittedSeverity) {
      this.notification.showToast(
        'O achado não traz a severidade com que saiu: classifique por outra categoria ou abra o review original.',
        'warning'
      );
      return;
    }
    if (this.asksDueSeverity && (!this.dueSeverity || this.dueSeverity === this.emittedSeverity)) {
      this.notification.showToast('Informe a severidade em que o achado deveria ter saído.', 'warning');
      return;
    }

    const body: CurateRequest = {
      resourceType: this.item.resourceType,
      resourceId: this.item.resourceId,
      findingKind: this.item.findingKind,
      findingId: this.item.findingId,
      kind: this.item.kind,
      category: this.category,
      curatorComment: this.commentToSave()
    };

    this.saving = true;
    const curatedItem = this.item;
    this.curationService.curateItem(body).subscribe({
      next: result => {
        this.saving = false;
        const curatedBy = result?.curatedBy?.trim();
        this.notification.showToast(
          curatedBy ? `✓ Curado por ${curatedBy}` : 'Curadoria salva com sucesso.',
          'success'
        );
        this.modal.close();
        this.curated.emit({ item: curatedItem, result });
      },
      error: error => {
        this.saving = false;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao salvar a curadoria.'), 'error');
      }
    });
  }
}
